import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Music, Loader2, Calendar, Users, BarChart3, FileText } from 'lucide-react';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { LanguageToggle } from '@/components/landing/LanguageToggle';
import { useUserProfile } from '@/contexts/UserProfileContext';

const AUTH_TIMEOUT_MS = 15_000;

async function withAuthTimeout<T>(operation: Promise<T>, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), AUTH_TIMEOUT_MS);
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

const authTexts = {
  it: {
    welcome: 'Benvenuto',
    signInOrCreate: 'Accedi o crea un account per continuare',
    signIn: 'Accedi',
    signUp: 'Registrati',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'tu@esempio.com',
    passwordPlaceholder: 'Min 8 caratteri, maiuscola, minuscola, numero',
    signingIn: 'Accesso...',
    creatingAccount: 'Creazione account...',
    createAccount: 'Crea Account',
    forgotPassword: 'Password dimenticata?',
    recoverPassword: 'Recupera Password',
    recoverDescription: 'Inserisci la tua email e ti invieremo un link per reimpostare la password.',
    cancelBtn: 'Annulla',
    sendEmail: 'Invia Email',
    sending: 'Invio...',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Termini di Servizio',
    welcomeBack: 'Bentornato!',
    invalidCredentials: 'Email o password non corretti',
    alreadyRegistered: 'Questa email è già registrata. Prova ad accedere.',
    signUpError: 'Errore durante la registrazione',
    signInError: "Errore durante l'accesso",
    authTimeout: 'Il servizio di accesso non risponde. Riprova tra qualche secondo.',
    emailSent: 'Email inviata! Controlla la tua casella di posta.',
    emailError: "Errore nell'invio dell'email",
    invalidEmail: 'Email non valida',
    emailTooLong: 'Email troppo lunga',
    passwordRequired: 'Password richiesta',
    passwordMin: 'La password deve avere almeno 8 caratteri',
    passwordTooLong: 'Password troppo lunga',
    passwordUppercase: 'Deve contenere almeno una lettera maiuscola',
    passwordLowercase: 'Deve contenere almeno una lettera minuscola',
    passwordNumber: 'Deve contenere almeno un numero',
    headline: "La piattaforma completa per",
    headlineSub: "l'industria musicale",
    tagline: 'Gestisci concerti, contatti, revenue shares e contratti in un\'unica piattaforma professionale.',
    concertMgmt: 'Gestione Concerti',
    crmContacts: 'CRM Contatti',
    advAnalytics: 'Analytics Avanzate',
    digitalContracts: 'Contratti Digitali',
    concertsManaged: 'Concerti gestiti',
    revenueTracked: 'Revenue tracciata',
  },
  en: {
    welcome: 'Welcome',
    signInOrCreate: 'Sign in or create an account to continue',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'you@example.com',
    passwordPlaceholder: 'Min 8 chars, uppercase, lowercase, number',
    signingIn: 'Signing in...',
    creatingAccount: 'Creating account...',
    createAccount: 'Create Account',
    forgotPassword: 'Forgot password?',
    recoverPassword: 'Recover Password',
    recoverDescription: "Enter your email and we'll send you a link to reset your password.",
    cancelBtn: 'Cancel',
    sendEmail: 'Send Email',
    sending: 'Sending...',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    welcomeBack: 'Welcome back!',
    invalidCredentials: 'Invalid email or password',
    alreadyRegistered: 'This email is already registered. Try signing in.',
    signUpError: 'Error during registration',
    signInError: 'Error during sign in',
    authTimeout: 'The sign-in service is not responding. Please try again in a few seconds.',
    emailSent: 'Email sent! Check your inbox.',
    emailError: 'Error sending email',
    invalidEmail: 'Invalid email',
    emailTooLong: 'Email too long',
    passwordRequired: 'Password required',
    passwordMin: 'Password must be at least 8 characters',
    passwordTooLong: 'Password too long',
    passwordUppercase: 'Must contain at least one uppercase letter',
    passwordLowercase: 'Must contain at least one lowercase letter',
    passwordNumber: 'Must contain at least one number',
    headline: 'The complete platform for',
    headlineSub: 'the music industry',
    tagline: 'Manage concerts, contacts, revenue shares and contracts in one professional platform.',
    concertMgmt: 'Concert Management',
    crmContacts: 'CRM Contacts',
    advAnalytics: 'Advanced Analytics',
    digitalContracts: 'Digital Contracts',
    concertsManaged: 'Concerts managed',
    revenueTracked: 'Revenue tracked',
  },
};

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const { lang } = useLandingLanguage();
  const { user } = useUserProfile();
  const at = authTexts[lang];

  const defaultTab = searchParams.get('tab') === 'signup' ? 'signup' : 'signin';
  const redirectPath = searchParams.get('redirect') || '/dashboard';

  const signInSchema = z.object({
    email: z.string().trim().email(at.invalidEmail).max(255, at.emailTooLong),
    password: z.string().min(1, at.passwordRequired),
  });

  const signUpSchema = z.object({
    email: z.string().trim().email(at.invalidEmail).max(255, at.emailTooLong),
    password: z.string()
      .min(8, at.passwordMin)
      .max(100, at.passwordTooLong)
      .regex(/[A-Z]/, at.passwordUppercase)
      .regex(/[a-z]/, at.passwordLowercase)
      .regex(/[0-9]/, at.passwordNumber),
  });

  const forgotPasswordSchema = z.object({
    email: z.string().trim().email(at.invalidEmail),
  });
  
  const signInForm = useForm<z.infer<typeof signInSchema>>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  const signUpForm = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: '', password: '' },
  });

  const forgotPasswordForm = useForm<z.infer<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (user) navigate(redirectPath, { replace: true });
  }, [navigate, redirectPath, user]);

  const handleSignIn = async (data: z.infer<typeof signInSchema>) => {
    try {
      const { error } = await withAuthTimeout(
        supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        }),
        at.authTimeout,
      );
      if (error) {
        if (error.message.includes('Invalid login credentials')) throw new Error(at.invalidCredentials);
        throw error;
      }
      toast.success(at.welcomeBack);
      navigate(redirectPath);
    } catch (error: any) {
      toast.error(error.message || at.signInError);
    }
  };

  const handleSignUp = async (data: z.infer<typeof signUpSchema>) => {
    try {
      const { error } = await withAuthTimeout(
        supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: { emailRedirectTo: `${window.location.origin}/auth/confirm?next=/onboarding` },
        }),
        at.authTimeout,
      );
      if (error) {
        if (error.message.includes('already registered')) throw new Error(at.alreadyRegistered);
        throw error;
      }
      sessionStorage.setItem('pendingConfirmEmail', data.email);
      navigate(`/confirm-email?email=${encodeURIComponent(data.email)}`);
    } catch (error: any) {
      toast.error(error.message || at.signUpError);
    }
  };

  const handleForgotPassword = async (data: z.infer<typeof forgotPasswordSchema>) => {
    setIsSendingReset(true);
    try {
      const { error } = await withAuthTimeout(
        supabase.auth.resetPasswordForEmail(data.email, {
          redirectTo: `${window.location.origin}/reset-password`,
        }),
        at.authTimeout,
      );
      if (error) throw error;
      toast.success(at.emailSent);
      setForgotPasswordOpen(false);
      forgotPasswordForm.reset();
    } catch (error: any) {
      toast.error(error.message || at.emailError);
    } finally {
      setIsSendingReset(false);
    }
  };

  const features = [
    { icon: Calendar, text: at.concertMgmt },
    { icon: Users, text: at.crmContacts },
    { icon: BarChart3, text: at.advAnalytics },
    { icon: FileText, text: at.digitalContracts },
  ];

  return (
    <div className="min-h-dvh min-w-0 overflow-x-clip flex">
      {/* Language toggle - top right corner */}
      <div className="fixed right-3 top-3 z-20 sm:right-4 sm:top-4">
        <LanguageToggle />
      </div>

      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-gradient-to-br from-primary via-primary/90 to-accent relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="musicPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="currentColor" />
                <circle cx="25" cy="25" r="1.5" fill="currentColor" />
                <circle cx="75" cy="75" r="1.5" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#musicPattern)" />
          </svg>
        </div>
        
        <div className="absolute top-20 left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-accent/20 rounded-full blur-2xl" />
        
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 text-primary-foreground">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Music className="h-8 w-8" />
            </div>
            <span className="text-3xl font-bold tracking-tight">Maestro CRM</span>
          </div>
          
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-6">
            {at.headline}
            <span className="block text-white/90">{at.headlineSub}</span>
          </h1>
          
          <p className="text-lg xl:text-xl text-primary-foreground/80 mb-12 max-w-md">
            {at.tagline}
          </p>
          
          <div className="grid grid-cols-2 gap-4 max-w-md">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 transition-all hover:bg-white/15"
              >
                <feature.icon className="h-5 w-5 text-white/90" />
                <span className="text-sm font-medium">{feature.text}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-16 pt-8 border-t border-white/20">
            <div className="flex items-center gap-8">
              <div>
                <div className="text-3xl font-bold">500+</div>
                <div className="text-sm text-primary-foreground/70">{at.concertsManaged}</div>
              </div>
              <div className="h-12 w-px bg-white/20" />
              <div>
                <div className="text-3xl font-bold">€2M+</div>
                <div className="text-sm text-primary-foreground/70">{at.revenueTracked}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full min-w-0 lg:w-1/2 xl:w-2/5 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-4 pb-6 pt-20 sm:p-8 lg:p-12">
          <div className="lg:hidden flex items-center gap-2 mb-6 sm:mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
              <Music className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold">Maestro CRM</span>
          </div>

          <Card className="w-full min-w-0 max-w-md border-border/50 shadow-lg">
            <CardHeader className="text-center space-y-2 pb-4">
              <CardTitle className="text-2xl">{at.welcome}</CardTitle>
              <CardDescription>{at.signInOrCreate}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={defaultTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="signin">{at.signIn}</TabsTrigger>
                  <TabsTrigger value="signup">{at.signUp}</TabsTrigger>
                </TabsList>
                
                <TabsContent value="signin" className="mt-0">
                  <Form {...signInForm}>
                    <form onSubmit={signInForm.handleSubmit(handleSignIn)} className="space-y-4">
                      <FormField
                        control={signInForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{at.email}</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder={at.emailPlaceholder} className="h-11" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signInForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{at.password}</FormLabel>
                            <FormControl>
                              <Input type="password" className="h-11" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button type="submit" className="w-full h-11 text-base font-medium" disabled={signInForm.formState.isSubmitting}>
                        {signInForm.formState.isSubmitting ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{at.signingIn}</>
                        ) : at.signIn}
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        className="w-full text-sm text-muted-foreground hover:text-primary"
                        onClick={() => setForgotPasswordOpen(true)}
                      >
                        {at.forgotPassword}
                      </Button>
                    </form>
                  </Form>
                </TabsContent>
                
                <TabsContent value="signup" className="mt-0">
                  <Form {...signUpForm}>
                    <form onSubmit={signUpForm.handleSubmit(handleSignUp)} className="space-y-4">
                      <FormField
                        control={signUpForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{at.email}</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder={at.emailPlaceholder} className="h-11" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signUpForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{at.password}</FormLabel>
                            <FormControl>
                              <Input type="password" placeholder={at.passwordPlaceholder} className="h-11" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button type="submit" className="w-full h-11 text-base font-medium" disabled={signUpForm.formState.isSubmitting}>
                        {signUpForm.formState.isSubmitting ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{at.creatingAccount}</>
                        ) : at.createAccount}
                      </Button>
                    </form>
                  </Form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-sm text-muted-foreground">
            <a href="/privacy" className="hover:text-foreground transition-colors">{at.privacyPolicy}</a>
            <span className="mx-2">|</span>
            <a href="/terms" className="hover:text-foreground transition-colors">{at.termsOfService}</a>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{at.recoverPassword}</DialogTitle>
            <DialogDescription>{at.recoverDescription}</DialogDescription>
          </DialogHeader>
          <Form {...forgotPasswordForm}>
            <form onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)} className="space-y-4">
              <FormField
                control={forgotPasswordForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{at.email}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder={at.emailPlaceholder} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotPasswordOpen(false)}>
                  {at.cancelBtn}
                </Button>
                <Button type="submit" disabled={isSendingReset}>
                  {isSendingReset ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{at.sending}</>
                  ) : at.sendEmail}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
